import { FC, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Box, Flex, Text } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/modules/api";
import { Button } from "@/components/button";
import { startCase } from "lodash-es";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import { atlasRoutes } from "@/app/router/routes";
import {
  CollectionListSmartCard,
  CollectionSmartCardItem,
  EventSmartEntityItem,
  MentionedBySmartCard,
  NoteListSmartCard,
  NoteSmartCardItem,
  SmartCard,
  EventTimelineSmartCard,
  isCollectionListSmartCard,
  isMarkdownSmartCard,
  isMentionedBySmartCard,
  isNoteListSmartCard,
  isEventTimelineSmartCard,
} from "./types";
import { SmartCardSchema } from "@/domains/openapi/types";

const NoteList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
`;

const CollectionList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
`;

const EventTimelineList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
`;

const DebugBox = styled(Box)`
  margin-top: 16px;
  border: 1px dashed var(--gray-6);
  padding: 12px;
  border-radius: 8px;
`;

const DebugList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

const formatTimestamp = (isoString: string | null | undefined) => {
  if (!isoString) {
    return "Unknown";
  }
  const dt = DateTime.fromISO(isoString, { zone: "utc" });
  if (!dt.isValid) {
    return isoString;
  }
  const relative = dt.toRelative({ base: DateTime.utc(), style: "short" }) ?? "just now";
  const absolute = dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  return `${relative} (${absolute})`;
};

const mapNoteItems = (raw: Record<string, unknown>): NoteSmartCardItem[] => {
  const items = raw.items as Record<string, unknown>[] | undefined;
  if (Array.isArray(items) && items.length > 0) {
    return items.map(item => ({
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      note_id: String(item.note_id ?? item.id ?? ""),
      primary_label: (item.primary_label as string) ?? (item.title as string) ?? "",
      secondary_label:
        (item.secondary_label as string | null | undefined) ??
        (item.snippet as string | null | undefined) ??
        null,
      internal_reason_for_selection:
        (item.internal_reason_for_selection as string | null | undefined) ?? null,
      kind: "NOTE",
      sort_key: (item.sort_key as string | null | undefined) ?? undefined,
    }));
  }
  return [];
};

const mapCollectionItems = (raw: Record<string, unknown>): CollectionSmartCardItem[] => {
  const items = raw.items as Record<string, unknown>[] | undefined;
  if (Array.isArray(items) && items.length > 0) {
    return items.map(item => ({
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      collection_id: String(item.collection_id ?? item.id ?? ""),
      primary_label: (item.primary_label as string) ?? (item.title as string) ?? "",
      secondary_label: (item.secondary_label as string | null | undefined) ?? null,
      internal_reason_for_selection:
        (item.internal_reason_for_selection as string | null | undefined) ?? null,
      kind: "COLLECTION",
      sort_key: (item.sort_key as string | null | undefined) ?? undefined,
    }));
  }
  return [];
};

const normalizeDateTime = (value: unknown): string => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  return new Date(0).toISOString();
};

const mapEventTimelineItems = (raw: Record<string, unknown>): EventSmartEntityItem[] => {
  const items = raw.items as Record<string, unknown>[] | undefined;
  if (Array.isArray(items) && items.length > 0) {
    return items.map(item => ({
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      event_smart_entity_id: String(item.event_smart_entity_id ?? item.id ?? ""),
      primary_label: (item.primary_label as string) ?? (item.title as string) ?? "",
      secondary_label:
        (item.secondary_label as string | null | undefined) ??
        (item.timestamp as string | null | undefined) ??
        null,
      internal_reason_for_selection:
        (item.internal_reason_for_selection as string | null | undefined) ?? null,
      linked_note_id:
        (item.linked_note_id as string | null | undefined) ??
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        (item.type === "note" && item.note_id ? String(item.note_id) : null),
      associated_datetime: normalizeDateTime(item.associated_datetime),
      kind: "EVENT_SMART_ENTITY",
      sort_key: (item.sort_key as string | null | undefined) ?? null,
      linked_note_primary_label:
        (item.linked_note_primary_label as string | null | undefined) ?? null,
    }));
  }
  return [];
};

const normalizeCard = (card: SmartCard, details?: SmartCardSchema): SmartCard => {
  if (!details) {
    return card;
  }
  const valueJson = (details.value_json ?? {}) as Record<string, unknown>;

  if (isNoteListSmartCard(card)) {
    if (card.value.items.length === 0) {
      return {
        ...card,
        value: {
          ...card.value,
          title: card.value.title ?? (valueJson.title as string | undefined) ?? null,
          items: mapNoteItems(valueJson),
          internal_reason_for_selection:
            card.value.internal_reason_for_selection ??
            (valueJson.internal_reason_for_selection as string | undefined) ??
            null,
        },
      };
    }
    return card;
  }

  if (isMentionedBySmartCard(card)) {
    if (card.value.items.length === 0) {
      return {
        ...card,
        value: {
          ...card.value,
          title: card.value.title ?? (valueJson.title as string | undefined) ?? null,
          items: mapNoteItems(valueJson),
        },
      };
    }
    return card;
  }

  if (isCollectionListSmartCard(card)) {
    if (card.value.items.length === 0) {
      return {
        ...card,
        value: {
          ...card.value,
          title: card.value.title ?? (valueJson.title as string | undefined) ?? null,
          items: mapCollectionItems(valueJson),
          internal_reason_for_selection:
            card.value.internal_reason_for_selection ??
            (valueJson.internal_reason_for_selection as string | undefined) ??
            null,
        },
      };
    }
    return card;
  }

  if (isEventTimelineSmartCard(card)) {
    if (card.value.items.length === 0) {
      return {
        ...card,
        value: {
          ...card.value,
          title: card.value.title ?? (valueJson.title as string | undefined) ?? null,
          items: mapEventTimelineItems(valueJson),
          internal_reason_for_selection:
            card.value.internal_reason_for_selection ??
            (valueJson.internal_reason_for_selection as string | undefined) ??
            null,
        },
      };
    }
    return card;
  }

  if (isMarkdownSmartCard(card)) {
    if (!card.value.markdown && typeof valueJson.markdown === "string") {
      return {
        ...card,
        value: {
          ...card.value,
          markdown: valueJson.markdown,
          title: card.value.title ?? (valueJson.title as string | undefined) ?? null,
        },
      };
    }
    return card;
  }

  return card;
};

const NoteListCard: FC<{ card: NoteListSmartCard; showDebug?: boolean }> = ({
  card,
  showDebug,
}) => (
  <Box>
    <Text as="div" size="1" color="gray">
      {card.value.items.length} {card.value.items.length === 1 ? "note" : "notes"}
    </Text>
    <NoteList>
      {card.value.items.map(item => (
        <NoteListEntry key={item.note_id} item={item} />
      ))}
    </NoteList>
    {showDebug && card.value.internal_reason_for_selection && (
      <Text size="1" color="gray">
        Reason: {card.value.internal_reason_for_selection}
      </Text>
    )}
  </Box>
);

const MentionedByCard: FC<{ card: MentionedBySmartCard }> = ({ card }) => (
  <Box>
    <NoteList>
      {card.value.items.map(item => (
        <NoteListEntry key={item.note_id} item={item} showSecondary />
      ))}
    </NoteList>
  </Box>
);

const CollectionListCard: FC<{ card: CollectionListSmartCard; showDebug?: boolean }> = ({
  card,
  showDebug,
}) => (
  <Box>
    <CollectionList>
      {card.value.items.map(item => (
        <CollectionListEntry key={item.collection_id} item={item} />
      ))}
    </CollectionList>
    {showDebug && card.value.internal_reason_for_selection && (
      <Text size="1" color="gray">
        Reason: {card.value.internal_reason_for_selection}
      </Text>
    )}
  </Box>
);

const EventTimelineCard: FC<{ card: EventTimelineSmartCard; showDebug?: boolean }> = ({
  card,
  showDebug,
}) => (
  <Box>
    <EventTimelineList>
      {card.value.items.map(item => (
        <EventTimelineEntry key={item.event_smart_entity_id} item={item} />
      ))}
    </EventTimelineList>
    {showDebug && card.value.internal_reason_for_selection && (
      <Text size="1" color="gray">
        Reason: {card.value.internal_reason_for_selection}
      </Text>
    )}
  </Box>
);

const MarkdownCard: FC<{ markdown?: string | null; title?: string | null }> = ({
  markdown,
  title: _title,
}) => (
  <Box>
    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{markdown ?? ""}</pre>
  </Box>
);

export const SmartCardViewer: FC<{ card: SmartCard; showDebug?: boolean }> = ({
  card,
  showDebug,
}) => {
  if (isNoteListSmartCard(card)) {
    return <NoteListCard card={card} showDebug={showDebug} />;
  }
  if (isMentionedBySmartCard(card)) {
    return <MentionedByCard card={card} />;
  }
  if (isCollectionListSmartCard(card)) {
    return <CollectionListCard card={card} showDebug={showDebug} />;
  }
  if (isEventTimelineSmartCard(card)) {
    return <EventTimelineCard card={card} showDebug={showDebug} />;
  }
  if (isMarkdownSmartCard(card)) {
    return <MarkdownCard markdown={card.value.markdown} title={card.value.title} />;
  }
  return (
    <Box>
      <Text as="div" color="gray">
        Unknown card kind ({card.kind}). Raw value:
      </Text>
      <pre>{JSON.stringify(card, null, 2)}</pre>
    </Box>
  );
};

interface SmartCardPanelProps {
  card: SmartCard;
  associatedSpaceAccountId: string;
  onCardRefreshed?: () => void;
}

export const SmartCardPanel: FC<SmartCardPanelProps> = ({ card }) => {
  const cardDetailsQuery = useQuery({
    queryKey: ["smart-card", card.id],
    queryFn: async () => {
      const resp = await api.get("/internal/v1/smart-cards/{smart_card_id}", {
        params: { path: { smart_card_id: card.id } },
      });
      return resp.data!;
    },
  });

  const [showDebug, setShowDebug] = useState(false);

  const normalizedCard = useMemo(
    () => normalizeCard(card, cardDetailsQuery.data),
    [card, cardDetailsQuery.data]
  );

  const cardTitle = useMemo(() => {
    if (
      isNoteListSmartCard(normalizedCard) ||
      isMentionedBySmartCard(normalizedCard) ||
      isCollectionListSmartCard(normalizedCard) ||
      isEventTimelineSmartCard(normalizedCard) ||
      isMarkdownSmartCard(normalizedCard)
    ) {
      const title = normalizedCard.value.title;
      if (title && title.length > 0) {
        return title;
      }
    }
    if (cardDetailsQuery.data?.derived_internal_label) {
      return cardDetailsQuery.data.derived_internal_label;
    }
    return `Card ${normalizedCard.sort_key}`;
  }, [normalizedCard, cardDetailsQuery.data?.derived_internal_label]);

  const friendlyKind = useMemo(
    () => startCase(normalizedCard.kind.toLowerCase()),
    [normalizedCard.kind]
  );
  const formattedRefreshedAt = useMemo(
    () => formatTimestamp(cardDetailsQuery.data?.refreshed_at ?? null),
    [cardDetailsQuery.data?.refreshed_at]
  );

  return (
    <CardRowBox>
      <Flex align="center" justify="between" gap="3">
        <Box>
          <Text size="3" weight="bold">
            {cardTitle}
          </Text>
          <Text as="div" size="1" color="gray">
            {friendlyKind}
            {formattedRefreshedAt ? ` · Refreshed ${formattedRefreshedAt}` : ""}
          </Text>
        </Box>
        <Flex gap="2">
          <Button variant="outline" size="1" onClick={() => setShowDebug(value => !value)}>
            {showDebug ? "Hide Debug" : "Show Debug"}
          </Button>
        </Flex>
      </Flex>

      <Box mt="2">
        {cardDetailsQuery.isLoading && <div>Loading card details…</div>}
        {cardDetailsQuery.isError && (
          <Text as="div" size="1" color="red">
            Failed to load card details
          </Text>
        )}
        <SmartCardViewer card={normalizedCard} showDebug={showDebug} />
      </Box>

      {showDebug && (
        <DebugBox>
          <Text as="div" size="1" weight="bold">
            Debug Details
          </Text>
          <DebugList>
            <li>
              <strong>Smart card ID:</strong> {normalizedCard.id}
            </li>
            <li>
              <strong>Sort key:</strong> {normalizedCard.sort_key}
            </li>
            <li>
              <strong>Smart view ID:</strong> {normalizedCard.smart_view_id}
            </li>
          </DebugList>
          <Box mt="2">
            <Text as="div" size="1" weight="bold">
              Rendered card
            </Text>
            <pre>{JSON.stringify(normalizedCard, null, 2)}</pre>
          </Box>
          {cardDetailsQuery.data && (
            <Box mt="2">
              <Text as="div" size="1" weight="bold">
                Raw value_json
              </Text>
              <pre>{JSON.stringify(cardDetailsQuery.data.value_json, null, 2)}</pre>
            </Box>
          )}
        </DebugBox>
      )}
    </CardRowBox>
  );
};

const CardRowBox = styled(Box)`
  border: 1px solid var(--gray-6);
  border-radius: 8px;
  padding: 12px;
`;

const NoteListEntry: FC<{ item: NoteSmartCardItem; showSecondary?: boolean }> = ({
  item,
  showSecondary,
}) => (
  <li>
    <StyledLink to={atlasRoutes.notesView({ params: { noteId: item.note_id } }).path}>
      <Text as="div" size="1">
        {item.primary_label}
      </Text>
    </StyledLink>
    {showSecondary && (
      <Text as="div" size="1" color="gray">
        {item.secondary_label}
      </Text>
    )}
  </li>
);

const CollectionListEntry: FC<{ item: CollectionSmartCardItem }> = ({ item }) => (
  <li>
    <StyledLink
      to={atlasRoutes.collectionsView({ params: { collectionId: item.collection_id } }).path}
    >
      <Text as="div" size="1">
        {item.primary_label}
      </Text>
    </StyledLink>
  </li>
);

const EventTimelineEntry: FC<{ item: EventSmartEntityItem }> = ({ item }) => (
  <li>
    <Text as="div" size="1">
      {item.primary_label}
      {item.linked_note_id && (
        <>
          {" "}
          <StyledLink to={atlasRoutes.notesView({ params: { noteId: item.linked_note_id } }).path}>
            [Note]
          </StyledLink>
        </>
      )}
    </Text>
    {item.associated_datetime && (
      <Text as="div" size="1" color="gray">
        {formatTimestamp(item.associated_datetime)}
      </Text>
    )}
  </li>
);
